// ============================================================================
// BrightPath - useNotifications Hook
// BP-12-03: Notifications Frontend
// ============================================================================

import { useCallback, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { notificationsApi } from '../api/notificationsApi';
import { useNotificationStore } from '../stores/notificationStore';
import type {
  Notification,
  NotificationListParams,
  NotificationCategory,
  UpdatePreferencesRequest,
  UpdateGlobalSettingsRequest,
} from '../types/notifications';

// ----------------------------------------------------------------------------
// Query Keys
// ----------------------------------------------------------------------------

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params: NotificationListParams) =>
    [...notificationKeys.lists(), params] as const,
  infiniteList: (params: NotificationListParams) =>
    [...notificationKeys.lists(), 'infinite', params] as const,
  detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
  globalSettings: () => [...notificationKeys.all, 'global-settings'] as const,
  pushStatus: () => [...notificationKeys.all, 'push-status'] as const,
};

// ----------------------------------------------------------------------------
// Main Hook
// ----------------------------------------------------------------------------

export function useNotifications(params: NotificationListParams = {}) {
  const queryClient = useQueryClient();
  const store = useNotificationStore();

  // --------------------------------------------------------------------------
  // Queries
  // --------------------------------------------------------------------------

  // Fetch notifications list
  const notificationsQuery = useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => {
      const response = await notificationsApi.list(params);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

  // Sync to store when data changes
  useEffect(() => {
    if (notificationsQuery.data) {
      const { notifications, page, pageSize, totalPages, total, unreadCount } =
        notificationsQuery.data;
      store.setNotifications(notifications);
      store.setPagination({ page, pageSize, totalPages, totalCount: total });
      store.setUnreadCount(unreadCount);
    }
  }, [notificationsQuery.data]);

  // Fetch unread count
  const unreadCountQuery = useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await notificationsApi.getUnreadCount();
      return response.data;
    },
    staleTime: 10000, // 10 seconds
    refetchInterval: 60000, // Refetch every minute
  });

  // Sync unread count to store
  useEffect(() => {
    if (unreadCountQuery.data) {
      store.setUnreadCount(unreadCountQuery.data.total);
      store.setUnreadByCategory(unreadCountQuery.data.byCategory);
    }
  }, [unreadCountQuery.data]);

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  // Mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationsApi.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      // Optimistic update
      store.markAsRead(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
    onError: (error, notificationId) => {
      // Revert on error - refetch to get accurate state
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      console.error('Failed to mark as read:', error);
    },
  });

  // Mark multiple notifications as read
  const markMultipleAsReadMutation = useMutation({
    mutationFn: (notificationIds: string[]) =>
      notificationsApi.markMultipleAsRead({ notificationIds }),
    onMutate: async (notificationIds) => {
      store.markMultipleAsRead(notificationIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onMutate: async () => {
      store.markAllAsRead();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
  });

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationsApi.delete(notificationId),
    onMutate: async (notificationId) => {
      store.removeNotification(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    },
  });

  // --------------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------------

  const markAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation]
  );

  const markMultipleAsRead = useCallback(
    (notificationIds: string[]) => {
      markMultipleAsReadMutation.mutate(notificationIds);
    },
    [markMultipleAsReadMutation]
  );

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback(
    (notificationId: string) => {
      deleteNotificationMutation.mutate(notificationId);
    },
    [deleteNotificationMutation]
  );

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
    queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  }, [queryClient]);

  // --------------------------------------------------------------------------
  // Return
  // --------------------------------------------------------------------------

  return {
    // Data
    notifications: store.notifications,
    unreadCount: store.unreadCount,
    unreadByCategory: store.unreadByCategory,
    
    // Pagination
    page: store.page,
    pageSize: store.pageSize,
    totalPages: store.totalPages,
    totalCount: store.totalCount,
    hasMore: store.hasMore,
    
    // Loading states
    isLoading: notificationsQuery.isLoading,
    isFetching: notificationsQuery.isFetching,
    isError: notificationsQuery.isError,
    error: notificationsQuery.error,
    
    // Actions
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    refetch,
    
    // Mutation states
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
  };
}

// ----------------------------------------------------------------------------
// Infinite Scroll Hook
// ----------------------------------------------------------------------------

export function useInfiniteNotifications(
  params: Omit<NotificationListParams, 'page'> = {}
) {
  const store = useNotificationStore();

  const query = useInfiniteQuery({
    queryKey: notificationKeys.infiniteList(params),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await notificationsApi.list({
        ...params,
        page: pageParam,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 30000,
  });

  // Flatten all pages into single array
  const allNotifications = query.data?.pages.flatMap((p) => p.notifications) ?? [];

  return {
    notifications: allNotifications,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
  };
}

// ----------------------------------------------------------------------------
// Preferences Hook
// ----------------------------------------------------------------------------

export function useNotificationPreferences() {
  const queryClient = useQueryClient();
  const store = useNotificationStore();

  // Fetch preferences
  const preferencesQuery = useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: async () => {
      const response = await notificationsApi.getPreferences();
      return response.data;
    },
    staleTime: 300000, // 5 minutes
  });

  // Fetch global settings
  const globalSettingsQuery = useQuery({
    queryKey: notificationKeys.globalSettings(),
    queryFn: async () => {
      const response = await notificationsApi.getGlobalSettings();
      return response.data;
    },
    staleTime: 300000,
  });

  // Sync to store
  useEffect(() => {
    if (preferencesQuery.data) {
      store.setPreferences(preferencesQuery.data);
    }
  }, [preferencesQuery.data]);

  useEffect(() => {
    if (globalSettingsQuery.data) {
      store.setGlobalSettings(globalSettingsQuery.data);
    }
  }, [globalSettingsQuery.data]);

  // Update preference mutation
  const updatePreferenceMutation = useMutation({
    mutationFn: (request: UpdatePreferencesRequest) =>
      notificationsApi.updatePreference(request),
    onSuccess: (response, request) => {
      store.updatePreference(request.category, response.data);
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
    },
  });

  // Update global settings mutation
  const updateGlobalSettingsMutation = useMutation({
    mutationFn: (request: UpdateGlobalSettingsRequest) =>
      notificationsApi.updateGlobalSettings(request),
    onSuccess: (response) => {
      store.setGlobalSettings(response.data);
      queryClient.invalidateQueries({ queryKey: notificationKeys.globalSettings() });
    },
  });

  return {
    preferences: store.preferences,
    globalSettings: store.globalSettings,
    isLoading: preferencesQuery.isLoading || globalSettingsQuery.isLoading,
    updatePreference: updatePreferenceMutation.mutate,
    updateGlobalSettings: updateGlobalSettingsMutation.mutate,
    isUpdating:
      updatePreferenceMutation.isPending || updateGlobalSettingsMutation.isPending,
  };
}

export default useNotifications;

